<template>
    <div
        class="content-wrapper position-relative"
    >
        <DebugContent
            title="DEBUG: Dynamic Page from CMS"
            class="ma-8"
        >
            <template #expand>
                <div
                    v-if="contentDisplayObj && !lo_isError(contentDisplayObj)"
                >
                    <h3
                        class="py-0 my-0"
                    >
                        Meta
                    </h3>
                    <ul
                        v-if="contentDisplayObj.meta"
                    >
                        <li
                            v-for="(v, k) in contentDisplayObj.meta"
                            :key="k"
                        >
                            <strong>{{ k }}:</strong>
                            {{ v }}
                        </li>
                    </ul>
                </div>
            </template>
        </DebugContent>

        <!-- Content / Error / Progress -->
        <template
            v-if="!currentError && contentDisplayObj && !lo_isError(contentDisplayObj) && contentDisplayObj.content && contentDisplayObj.meta && contentDisplayObj?.baseComponent"
        >
            <Suspense>
                <component
                    :is="contentComponents[contentDisplayObj.baseComponent]"
                    :page-key="pageKey"
                    :content-data="contentDisplayObj.content"
                    :content-meta="contentDisplayObj.meta"
                ></component>
            </Suspense>
        </template>
        <template
            v-else-if="currentError"
        >
            <ContentPageError :error="currentError"></ContentPageError>
        </template>
        <template
            v-else
        >
            <!-- Progress Indicator -->
            <div

                class="
                    d-flex
                    flex-column
                    align-center
                    justify-center
                    content-wrapper-progress
                    absolute-center
                    h-100
                "
            >
                <v-progress-circular
                    color="accent"
                    width="8"
                    size="62"
                    indeterminate
                ></v-progress-circular>
            </div>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import DebugContent from '~/components/DebugContent/DebugContent.vue';
    import ContentPageError from '~/components/error/ContentPageError/ContentPageError.vue';

    import type { ContentComponentType, GetContentDataResponse } from '~/types';
    import type { NuxtError } from '#app';
    import { useContent } from '~/composables';
    import { isError as lo_isError } from 'es-toolkit';
    import type { WatchStopHandle } from 'vue';
    import { useSeoMeta } from '#imports';
    import { pathJoin, getPseudoHash } from 'x1-base/utils';
    import { whenever } from '@vueuse/core';

    definePageMeta({
        scrollToTop: true,
        path: '/content/:catchAll(.*)',
        alias: [
            '/:catchAll(.*)'
        ]
    });

    const contentComponents: Record<ContentComponentType, Nullable<ReturnType<typeof resolveComponent>>> = {
        TypePage: resolveComponent('ContentTypePage'),
        TypeCollection: resolveComponent('ContentTypeCollection'),
        TypeArticle: resolveComponent('ContentTypeArticle'),
        TypeProduct: resolveComponent('ContentTypeProduct'),
        None: null
    };

    const $config = useRuntimeConfig();
    const route = useRoute();
    const { getPageContent } = useContent();
    const brandTitle = $config.public.brand.title;
    const baseURL = $config.public.environment.homeUrl;

    const pageKey = computed<string>(() => `keyContent_${getPseudoHash(route.fullPath)}`);

    async function getContent(): Promise<GetContentDataResponse | NuxtError> {
        const result = await getPageContent(route);

        if (lo_isError(result)) {
            throw result;
        }

        return result;
    }

    const contentAsyncData = useLazyAsyncData<GetContentDataResponse | NuxtError>(
        pageKey.value,
        () => getContent()
    );
    const {
        data: contentDisplayObj,
        error: currentError
    } = contentAsyncData;

    // On the server, wait for the CMS so the watchers below see the result while
    // the response can still be changed. Client navigation stays lazy.
    if (import.meta.server) {
        await contentAsyncData;
    }

    // Resolved page meta — null for errors, redirects, or while loading. The CMS
    // data resolves before the SSR render completes (the rendered H1 and product
    // JSON-LD prove it), so deriving the head from computeds — rather than setting
    // it inside a watcher, which never runs during SSR — puts <title>, the meta
    // description, and the OG/Twitter tags into the server-rendered HTML.
    const pageMeta = computed(() => {
        const obj = contentDisplayObj.value;
        if (!obj || lo_isError(obj) || !('meta' in obj) || obj.meta.type === 'redirect') {
            return null;
        }
        return obj.meta;
    });

    const docTitle = computed<Undefinable<string>>(() =>
        currentError.value
            ? `Page Not Found | ${brandTitle}`
            : (pageMeta.value ? `${pageMeta.value.title} | ${brandTitle}` : undefined)
    );
    const metaDescription = computed<Undefinable<string>>(() => pageMeta.value?.metaDescription ?? undefined);

    useHead({
        title: docTitle,
        meta: () => (metaDescription.value
            ? [{ name: 'description', content: metaDescription.value }]
            : [])
    });

    useSeoMeta({
        ogTitle: () => docTitle.value ?? null,
        twitterTitle: () => docTitle.value ?? null,
        ogUrl: () => `${baseURL}${route.fullPath}`,
        ogDescription: () => metaDescription.value ?? null,
        twitterDescription: () => metaDescription.value ?? null
    });

    // Side effects: server 301 for CMS redirect entries, and 404 / 503 status.
    // On the server these fire immediately because the data was awaited above.
    const unWatchContentDisplayObj = whenever(contentDisplayObj, handleContent, { immediate: true, once: true });
    const unWatchError = whenever(currentError, handleError, { immediate: true, once: true });

    async function handleContent(contentObj: GetContentDataResponse | NuxtError): Promise<void> {
        if (!lo_isError(contentObj) && contentObj.meta.type === 'redirect' && contentObj?.meta.path) {
            await navigateTo(pathJoin('/', contentObj.meta.path), { redirectCode: 301 });
        }
    }

    function handleError(errorObj: Error | NuxtError): void {
        if (isApiDown(errorObj)) {
            showError({ statusCode: 503, message: 'Site Offline', fatal: true });
        } else {
            setResponseStatus(useRequestEvent()!, 404, 'Page Not Found');
        }
    }

    function isApiDown(err: NuxtError | Error | void): boolean {
        return Boolean(err?.toString()?.includes('no response'));
    }

    onBeforeUnmount(() => {
        unWatchError?.();
        unWatchContentDisplayObj?.();
    });
</script>

<style lang="scss">
.content-wrapper {
    min-height: 60vh;
}
</style>
